// src/components/tokenPage/overview/PerformanceStrip.tsx

import React, { useMemo } from "react";
import { Perf } from "../shared/TokenDashboardComponents";
import { DataCard } from "@/components/ui";
import { useTokenFundamentals } from "@/contexts/token";

type WindowKey = '1d' | '7d' | '30d' | '60d' | '90d' | '180d' | '365d';

interface Props {
  /** WebSocket live price (from Binance) */
  livePrice: number | null;
  /** Snapshot price from backend */
  snapshotPrice: number | null;
  /** WebSocket 24h change as decimal (e.g., 0.05 for 5%) */
  ws24hChangeDecimal: number | null;
  /** Which windows to display */
  windows?: Array<{ label: string; key: WindowKey }>;
  gridClassName?: string;
  isLoading?: boolean;
}

/**
 * PerformanceStrip - displays price performance over various time windows
 * 
 * Data priority:
 * 1. WebSocket live data (ws24hChangeDecimal for 24h)
 * 2. Provider snapshot data (priceChanges from TokenFundamentalsContext)
 * 
 * The live price adjustment is applied to backend changes to reflect
 * current market price relative to each window's starting price.
 */
const PerformanceStrip: React.FC<Props> = ({
  livePrice,
  snapshotPrice,
  ws24hChangeDecimal,
  windows = [
    { label: '24h', key: '1d' },
    { label: '7d', key: '7d' },
    { label: '30d', key: '30d' },
    { label: '1y', key: '365d' },
  ],
  gridClassName = "grid grid-cols-2 gap-2",
  isLoading: externalLoading = false,
}) => {
  // Get price changes from context (fallback source)
  const { priceChanges, isLoading: fundamentalsLoading } = useTokenFundamentals();
  
  // Compute live-adjusted change from backend change
  const computeLive = useMemo(() => {
    return (reportedPctPercent: number | null) => {
      if (livePrice == null || snapshotPrice == null || reportedPctPercent == null) {
        return reportedPctPercent == null ? null : reportedPctPercent / 100;
      }
      const reportedPct = reportedPctPercent / 100;
      const base = snapshotPrice / (1 + reportedPct);
      if (Math.abs(base) < 1e-10) return reportedPct;
      return (livePrice / base) - 1;
    };
  }, [livePrice, snapshotPrice]);

  // Get the change value for a window
  const getWindowChange = (key: WindowKey): number | null => {
    // For 24h, prefer WebSocket data
    if (key === '1d' && ws24hChangeDecimal != null) {
      return ws24hChangeDecimal;
    }
    
    // Use provider data as source
    const backendChange = priceChanges[key];
    
    // Apply live price adjustment if we have both live price and backend data
    return computeLive(backendChange);
  };

  // Check if all values are null
  const hasAnyData = windows.some(({ key }) => getWindowChange(key) != null);

  const isLoading = externalLoading || fundamentalsLoading;

  // Hide the performance strip if no data (after loading)
  if (!isLoading && !hasAnyData) {
    return null;
  }

  return (
    <DataCard
      title="Performance"
      subtitle="Recent returns"
      icon="trending-up"
      iconColor="text-emerald-400"
      isLoading={isLoading}
      loadingVariant="stats"
    >
      <div className={gridClassName}>
        {windows.map(({ label, key }) => {
          const value = getWindowChange(key);
          return <Perf key={key} label={label} value={value} />;
        })}
      </div>
    </DataCard>
  );
};

export default PerformanceStrip;
